import * as Model from "@effect/sql/Model";
import { Schema } from "effect";

/**
 * One expectation-check verdict recorded for a simulator run: which check ran,
 * what the archetype's expectations predicted, what the recorded evidence showed,
 * and whether the two agree. Stored as JSONB since the array is always read and
 * written as a unit.
 * @since 1.0.0
 */
export const SimExpectationResultSchema = Schema.Struct({
  check: Schema.String,
  expected: Schema.String,
  observed: Schema.String,
  pass: Schema.Boolean,
});

/**
 * A single expectation-check verdict row.
 * @since 1.0.0
 */
export type SimExpectationResult = Schema.Schema.Type<typeof SimExpectationResultSchema>;

/**
 * The two ways a simulator run is driven: `manual` — a human operator sends each
 * message via RPC; `archetype` — a simulated flirter LLM plays the user.
 * @since 1.0.0
 */
export const SimRunModeSchema = Schema.Literal("manual", "archetype");

/**
 * The lifecycle state of a simulator run.
 * @since 1.0.0
 */
export const SimRunStatusSchema = Schema.Literal("running", "completed", "failed");

/**
 * Simulator run model: one recorded testbench conversation through the real scene
 * pipeline. Manual runs carry a null `archetypeKey`; archetype runs name the
 * archetype that drove them and accrue `expectationResults`. Both runs record the
 * record-only psyche `inference`. `error` is set only when a run fails.
 *
 * Single source of truth — never duplicate these fields elsewhere. IDs are plain
 * strings (no branded ids); timestamps use `utcTimestamp()` columns.
 * @since 1.0.0
 */
export class SimRun extends Model.Class<SimRun>("SimRun")({
  id: Model.Generated(Schema.String),
  mode: SimRunModeSchema,
  archetypeKey: Schema.NullOr(Schema.String),
  sceneKey: Schema.String,
  conversationId: Schema.String,
  status: SimRunStatusSchema,
  expectationResults: Schema.Array(SimExpectationResultSchema),
  inference: Schema.NullOr(Schema.String),
  error: Schema.NullOr(Schema.String),
  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
}) {}
